import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Download, Shield, CheckCircle, FileText, Globe, Users, Lock, AlertTriangle, Award, Eye, Scale, Building, DollarSign, Target, Zap, Database, RefreshCw, Mail, PhoneCall, ChevronDown, BadgeCheck, MapPin } from 'lucide-react';
import SignupModal from '../SignupModal';
import LoginModal from '../LoginModal';
import ActivateCardModal from '../ActivateCardModal';

const LicensesPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showActivateCardModal, setShowActivateCardModal] = useState(false);
  const [signupStep, setSignupStep] = useState('details');
  const [loginStep, setLoginStep] = useState('email');
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: 'US',
    code: '',
    generatedCode: ''
  });
  const [loginData, setLoginData] = useState({
    email: '',
    phone: '',
    loginMethod: 'email',
    code: '',
    generatedCode: ''
  });
  const [activateData, setActivateData] = useState({
    cardNumber: '',
    pin: '',
    code: '',
    generatedCode: ''
  });

  const countries = [
    { code: 'US', name: 'United States', requiresSSN: true },
    { code: 'CA', name: 'Canada', requiresSSN: false },
    { code: 'UK', name: 'United Kingdom', requiresSSN: false },
    { code: 'DE', name: 'Germany', requiresSSN: false },
    { code: 'FR', name: 'France', requiresSSN: false },
    { code: 'NG', name: 'Nigeria', requiresSSN: false },
    { code: 'GH', name: 'Ghana', requiresSSN: false },
    { code: 'KE', name: 'Kenya', requiresSSN: false },
    { code: 'ZA', name: 'South Africa', requiresSSN: false },
    { code: 'BR', name: 'Brazil', requiresSSN: false },
    { code: 'IN', name: 'India', requiresSSN: false }
  ];

  const menuItems = {
    Services: [
      { name: 'Send Money', path: '/send-money' },
      { name: 'Crypto Cards', path: '/crypto-cards' },
      { name: 'Digital Wallets', path: '/digital-wallets' },
      { name: 'Business Solutions', path: '/business-solutions' }
    ],
    Company: [
      { name: 'About Us', path: '/about-us' },
      { name: 'Careers', path: '/careers' },
      { name: 'Press', path: '/press' },
      { name: 'Investors', path: '/investors' }
    ],
    Support: [
      { name: 'Help Center', path: '/help-center' },
      { name: 'Contact Us', path: '/contact-us' },
      { name: 'API Documentation', path: '/api-documentation' },
      { name: 'Security', path: '/security' }
    ],
    Legal: [
      { name: 'Terms of Service', path: '/terms-of-service' },
      { name: 'Privacy Policy', path: '/privacy-policy' },
      { name: 'Compliance', path: '/compliance' },
      { name: 'Licenses', path: '/licenses' }
    ]
  };

  const toggleSubmenu = (menu) => {
    setExpandedMenu(expandedMenu === menu ? null : menu);
  };

  const generateAuthCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const setShowLandingPage = (value) => {
    console.log('Navigation would occur here:', value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      {/* Header */}
      <header className="bg-purple-900/50 backdrop-blur-sm border-b border-purple-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/zanors-logo-full.png" 
                alt="Zanors Logo" 
                className="h-16 md:h-20 mix-blend-normal" 
                style={{ backgroundColor: 'transparent' }}
              />
            </Link>

            {/* Action Buttons + Hamburger */}
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowSignupModal(true)}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors"
              >
                Sign Up
              </button>
              <button 
                onClick={() => setShowLoginModal(true)}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors"
              >
                Login
              </button>
              <button 
                onClick={() => setShowActivateCardModal(true)}
                className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors"
              >
                Activate Card
              </button>
              
              {/* Hamburger Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-purple-700 rounded-lg transition-colors"
                aria-label="Menu"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Hamburger Dropdown Menu */}
        {isMenuOpen && (
          <div className="bg-purple-800 border-t border-purple-700 max-h-96 overflow-y-auto">
            <div className="container mx-auto px-4 py-4">
              {Object.entries(menuItems).map(([category, items]) => (
                <div key={category} className="mb-2">
                  <button
                    onClick={() => toggleSubmenu(category)}
                    className="w-full flex items-center justify-between p-3 hover:bg-purple-700 rounded-lg transition-colors"
                  >
                    <span className="font-semibold text-lg">{category}</span>
                    <ChevronDown 
                      className={`w-5 h-5 transition-transform ${
                        expandedMenu === category ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {expandedMenu === category && (
                    <div className="ml-4 mt-2 space-y-1 bg-purple-900/50 rounded-lg p-2">
                      {items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`block p-3 hover:bg-purple-700 rounded-lg transition-colors ${
                            item.path === '/licenses' ? 'text-white font-medium bg-purple-700' : 'text-gray-200 hover:text-white'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Fixed QR Code on Right Center Margin */}
      <div 
        className="fixed right-0 top-1/2 transform -translate-y-1/2 z-40 cursor-pointer"
        onMouseEnter={() => setShowQRCode(true)}
        onMouseLeave={() => setShowQRCode(false)}
      >
        <div className={`transition-all duration-300 ${showQRCode ? 'w-48' : 'w-16'} bg-purple-800 border-l-4 border-green-400 rounded-l-lg shadow-lg`}>
          {!showQRCode ? (
            <div className="p-4">
              <div className="w-8 h-8 bg-white rounded grid grid-cols-2 gap-0.5 p-1">
                <div className="bg-black"></div>
                <div className="bg-white"></div>
                <div className="bg-white"></div>
                <div className="bg-black"></div>
              </div>
            </div>
          ) : (
            <div className="p-4">
              <h3 className="text-sm font-bold mb-2 text-center">Get the ZANORS App</h3>
              <div className="bg-white p-2 rounded mb-2">
                <div className="grid grid-cols-8 gap-0.5">
                  {[...Array(64)].map((_, i) => (
                    <div 
                      key={i} 
                      className={`aspect-square ${Math.random() > 0.5 ? 'bg-black' : 'bg-white'}`}
                    ></div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <a 
                  href="https://apps.apple.com/app/globalpay" 
                  className="block text-center bg-gray-900 hover:bg-gray-800 px-2 py-1 rounded text-xs transition-colors"
                >
                  App Store
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.globalpay.app" 
                  className="block text-center bg-gray-900 hover:bg-gray-800 px-2 py-1 rounded text-xs transition-colors"
                >
                  Google Play
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-600 rounded-full mb-6">
            <Award size={40} />
          </div>
          <h1 className="text-5xl font-bold mb-4">Licenses & Certifications</h1>
          <p className="text-xl text-purple-200 mb-4">
            Our Compliance Roadmap & Regulatory Strategy
          </p>
          <p className="text-purple-300 max-w-3xl mx-auto">
            ZANORS is a pre-launch fintech startup building toward full regulatory compliance across all target markets. This page outlines our comprehensive licensing roadmap — the exact registrations, certifications, and partnerships we are actively pursuing as part of our go-to-market compliance strategy.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-green-900 bg-opacity-50 rounded-lg p-6 text-center border border-green-500">
            <div className="text-4xl font-bold text-green-400 mb-2">50+</div>
            <p className="text-purple-200 text-sm">Target State Licenses</p>
          </div>
          <div className="bg-blue-900 bg-opacity-50 rounded-lg p-6 text-center border border-blue-500">
            <div className="text-4xl font-bold text-blue-400 mb-2">20+</div>
            <p className="text-purple-200 text-sm">Target Countries</p>
          </div>
          <div className="bg-purple-900 bg-opacity-50 rounded-lg p-6 text-center border border-purple-500">
            <div className="text-4xl font-bold text-purple-400 mb-2">10+</div>
            <p className="text-purple-200 text-sm">Planned Certifications</p>
          </div>
          <div className="bg-yellow-900 bg-opacity-50 rounded-lg p-6 text-center border border-yellow-500">
            <div className="text-4xl font-bold text-yellow-400 mb-2">Pre-Launch</div>
            <p className="text-purple-200 text-sm">Current Status</p>
          </div>
        </div>

        {/* Detailed Sections */}
        <div className="space-y-12">
          {/* Section 1 - Federal Licenses */}
          <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Building className="mr-3 text-blue-400" size={32} />
              1. Federal Licenses & Registrations (USA)
            </h2>

            <div className="bg-blue-900 bg-opacity-40 border border-blue-500 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-200">
                <strong>📋 Pre-Launch Compliance Status:</strong> ZANORS is currently in the pre-launch phase. The federal registrations and licenses listed below represent our planned compliance roadmap. Applications will be filed upon completion of seed funding and incorporation.
              </p>
            </div>
            
            <div className="space-y-6">
              {[
                {
                  icon: '🏛️',
                  title: 'FinCEN Money Services Business (MSB)',
                  color: 'text-blue-400',
                  border: 'border-blue-600',
                  description: 'Registration with the Financial Crimes Enforcement Network as a Money Services Business, authorizing money transmission, currency exchange, and cryptocurrency services.',
                  why: 'Required for all US-based money transmission operations. ZANORS will register with FinCEN as one of its first compliance steps post-incorporation.',
                  timeline: 'Target: Within 60 days of incorporation',
                },
                {
                  icon: '📜',
                  title: 'NMLS Money Transmitter License',
                  color: 'text-purple-400',
                  border: 'border-purple-600',
                  description: 'Nationwide Multistate Licensing System & Registry license enabling operation as a money transmitter across all 50 US states and territories.',
                  why: 'Central to our US expansion strategy. NMLS licensing provides a unified framework for multistate money transmission compliance.',
                  timeline: 'Target: Q3 post-funding',
                },
                {
                  icon: '🗽',
                  title: 'New York State BitLicense',
                  color: 'text-yellow-400',
                  border: 'border-yellow-600',
                  description: 'One of the most stringent cryptocurrency licenses in the United States, issued by the New York State Department of Financial Services (NYDFS), authorizing virtual currency business activity.',
                  why: 'New York is a key market. BitLicense compliance signals the highest standard of crypto regulation readiness to institutional partners and investors.',
                  timeline: 'Target: Q4 post-funding',
                },
              ].map(({ icon, title, color, border, description, why, timeline }) => (
                <div key={title} className={`bg-blue-900 bg-opacity-40 border ${border} rounded-lg p-6`}>
                  <div className="flex items-start mb-4">
                    <AlertTriangle className="mr-3 flex-shrink-0 text-yellow-400 mt-1" size={28} />
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className="text-xl">{icon}</span>
                        <h3 className="text-xl font-bold text-white">{title}</h3>
                        <span className="bg-yellow-900 text-yellow-300 text-xs font-bold px-3 py-1 rounded-full border border-yellow-600">
                          Application Planned
                        </span>
                      </div>
                      <p className="text-sm text-purple-200 mb-3">{description}</p>
                      <div className="bg-purple-900 bg-opacity-60 rounded-lg p-3 space-y-1">
                        <p className="text-xs text-purple-300"><strong className="text-purple-100">Why it matters:</strong> {why}</p>
                        <p className="text-xs text-yellow-400 font-semibold">⏱ {timeline}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2 - State Licenses */}
          <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <MapPin className="mr-3 text-blue-400" size={32} />
              2. US State Money Transmitter Licenses
            </h2>
            
            <div className="space-y-6 text-purple-200">
              <p className="mb-4">
                ZANORS is actively pursuing money transmitter licenses across all 50 US states, Washington D.C., and US territories. Our applications are currently under regulatory review.
              </p>

              <div className="bg-yellow-900 bg-opacity-40 border border-yellow-500 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-200">
                  <strong>⚠️ Notice:</strong> All state money transmitter license applications are currently in process. Licenses will be updated upon approval from each respective state regulatory authority.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California',
                  'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia',
                  'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
                  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
                  'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
                  'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
                  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
                  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
                  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
                  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
                  'Washington D.C.', 'Puerto Rico', 'US Virgin Islands', 'Guam',
                ].map((state, idx) => (
                  <div key={idx} className="bg-purple-900 bg-opacity-60 p-4 rounded-lg">
                    <div className="flex items-start">
                      <AlertTriangle className="mr-2 flex-shrink-0 text-yellow-400 mt-1" size={20} />
                      <div className="flex-1">
                        <h4 className="font-bold text-white">{state}</h4>
                        <p className="text-sm text-yellow-400 font-medium">Pending Registration</p>
                        <p className="text-xs text-purple-400">Application under review</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-900 bg-opacity-40 border border-blue-600 rounded-lg p-4 mt-6">
                <p className="text-sm">
                  <strong>Status:</strong> License applications are actively being processed with each state regulatory authority. We are committed to full compliance and will update this page as licenses are approved.
                </p>
              </div>
            </div>
          </div>

          {/* Section 3 - International Licenses */}
          <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Globe className="mr-3 text-cyan-400" size={32} />
              3. International Licenses & Authorizations
            </h2>

            <div className="bg-yellow-900 bg-opacity-40 border border-yellow-500 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-200">
                <strong>⚠️ Notice:</strong> All international license applications are currently under review by the respective regulatory authorities. This page will be updated as approvals are received.
              </p>
            </div>
            
            <div className="space-y-6">
              {[
                { flag: '🇪🇺', region: 'European Union', body: 'Central Bank of Ireland / EBA', licenses: ['E-Money Institution (EMI) License', 'MiFID II Investment Firm Authorization'] },
                { flag: '🇬🇧', region: 'United Kingdom', body: 'Financial Conduct Authority (FCA)', licenses: ['FCA Crypto Asset Registration', 'Electronic Money Regulations 2011 (SEMI)', 'Payment Services Regulations 2017 (API)'] },
                { flag: '🇨🇦', region: 'Canada', body: 'FINTRAC / Provincial Regulators', licenses: ['FINTRAC Money Services Business Registration', 'Ontario MSB License', 'Quebec AMF Registration', 'British Columbia License', 'Alberta Registration', 'All other provinces'] },
                { flag: '🇸🇬', region: 'Singapore', body: 'Monetary Authority of Singapore (MAS)', licenses: ['MAS Major Payment Institution License'] },
                { flag: '🇦🇺', region: 'Australia', body: 'AUSTRAC / ASIC', licenses: ['AUSTRAC Digital Currency Exchange Registration', 'ASIC Company Registration'] },
                { flag: '🇯🇵', region: 'Japan', body: 'Financial Services Agency (FSA)', licenses: ['FSA Cryptocurrency Exchange License'] },
                { flag: '🇨🇭', region: 'Switzerland', body: 'Swiss Financial Market Supervisory Authority (FINMA)', licenses: ['FINMA FinTech License'] },
              ].map(({ flag, region, body, licenses }) => (
                <div key={region} className="bg-blue-900 bg-opacity-40 border border-blue-600 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center">
                    <span className="mr-2">{flag}</span> {region}
                  </h3>
                  <p className="text-sm text-purple-300 mb-4">Regulatory Body: {body}</p>
                  <div className="space-y-2">
                    {licenses.map((lic) => (
                      <div key={lic} className="flex items-center">
                        <AlertTriangle className="mr-2 flex-shrink-0 text-yellow-400" size={16} />
                        <div>
                          <p className="text-sm text-purple-200 font-medium">{lic}</p>
                          <p className="text-xs text-yellow-400 font-semibold">Pending License</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="bg-blue-900 bg-opacity-40 border border-blue-600 rounded-lg p-4 mt-6">
                <p className="text-sm text-purple-200">
                  <strong>Status:</strong> International licensing applications are actively in progress. ZANORS is committed to obtaining all necessary authorizations in each jurisdiction prior to full market launch.
                </p>
              </div>
            </div>
          </div>

          {/* Section 4 - Industry Certifications */}
          <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Award className="mr-3 text-yellow-400" size={32} />
              4. Industry Certifications & Standards
            </h2>

            <div className="bg-yellow-900 bg-opacity-40 border border-yellow-600 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-200">
                <strong>🎯 Our Compliance Target:</strong> ZANORS is building toward the highest industry security and privacy standards from day one. The certifications below represent the framework we are engineering our platform against, with formal certification planned post-funding.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { icon:'🔐', color:'border-yellow-500', iconColor:'text-yellow-400', title:'ISO 27001:2022', sub:'Information Security Management System', desc:'International gold standard for information security. Our infrastructure and engineering practices are being designed to this specification from the ground up.', timeline:'Certification planned: Post-funding, Year 1' },
                { icon:'🛡️', color:'border-blue-500', iconColor:'text-blue-400', title:'SOC 2 Type II', sub:'Service Organization Control', desc:'Independent third-party audit of our security, availability, and confidentiality controls. Critical for enterprise and institutional client onboarding.', timeline:'Audit planned: Post-launch, Year 1' },
                { icon:'💳', color:'border-green-500', iconColor:'text-green-400', title:'PCI DSS Level 1', sub:'Payment Card Industry Data Security Standard', desc:'The highest tier of payment card security compliance, required for processing large transaction volumes. Our card infrastructure will be built to Level 1 specifications.', timeline:'Certification planned: Pre card-launch' },
                { icon:'⚙️', color:'border-purple-500', iconColor:'text-purple-400', title:'ISO 9001:2015', sub:'Quality Management System', desc:'International quality management certification ensuring consistent, reliable service delivery processes across all operational functions.', timeline:'Certification planned: Year 1' },
                { icon:'🇪🇺', color:'border-cyan-500', iconColor:'text-cyan-400', title:'GDPR Compliance', sub:'General Data Protection Regulation', desc:'Full EU data protection compliance is foundational to our European expansion. Our privacy architecture is being designed with GDPR as a baseline requirement.', timeline:'Privacy framework: In design' },
                { icon:'🇺🇸', color:'border-red-500', iconColor:'text-red-400', title:'CCPA/CPRA Compliance', sub:'California Consumer Privacy Act', desc:'California privacy law compliance is baked into our data handling architecture. Consumer rights management and data transparency are core product principles.', timeline:'Compliance framework: In design' },
              ].map(({ icon, color, iconColor, title, sub, desc, timeline }) => (
                <div key={title} className={`bg-purple-900 bg-opacity-60 p-6 rounded-lg border ${color}`}>
                  <div className="flex items-start mb-3">
                    <Shield className={`mr-3 flex-shrink-0 ${iconColor}`} size={28} />
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <span>{icon}</span>
                        <h3 className="text-lg font-bold text-white">{title}</h3>
                        <span className="bg-blue-900 text-blue-300 text-xs font-bold px-2 py-0.5 rounded-full border border-blue-700">Planned</span>
                      </div>
                      <p className="text-sm text-purple-300 mb-2">{sub}</p>
                      <p className="text-xs text-purple-200 leading-relaxed mb-2">{desc}</p>
                      <p className="text-xs text-yellow-400 font-semibold">⏱ {timeline}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 5 - Banking Relationships */}
          <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Building className="mr-3 text-green-400" size={32} />
              5. Banking Partnerships & Insurance
            </h2>

            <div className="bg-blue-900 bg-opacity-40 border border-blue-500 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-200">
                <strong>🤝 Partnership Strategy:</strong> ZANORS has identified and is actively pursuing relationships with best-in-class banking and insurance partners. The institutions below represent our target partners — selected for their fintech-friendly infrastructure and regulatory standing.
              </p>
            </div>

            <div className="space-y-6 text-purple-200">
              <div className="bg-green-900 bg-opacity-40 border border-green-600 rounded-lg p-6">
                <div className="flex items-start">
                  <Building className="mr-3 flex-shrink-0 text-green-400 mt-1" size={32} />
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <h3 className="text-xl font-bold text-white">Target Banking Partner: BankProv (FDIC Member)</h3>
                      <span className="bg-yellow-900 text-yellow-300 text-xs font-bold px-3 py-1 rounded-full border border-yellow-600">Outreach Planned</span>
                    </div>
                    <div className="space-y-2 text-sm mt-3">
                      <p>BankProv is a leading fintech-friendly FDIC-insured bank headquartered in Amesbury, Massachusetts with $3.8B+ in assets. They are our preferred partner for USD customer deposit accounts, providing FDIC insurance coverage up to $250,000 per depositor.</p>
                      <p className="text-xs text-purple-300 mt-2">Why BankProv: Established track record with crypto and fintech companies, robust API infrastructure, and regulatory-first approach aligns with ZANORS compliance strategy.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-900 bg-opacity-40 border border-blue-600 rounded-lg p-6">
                <div className="flex items-center flex-wrap gap-2 mb-4">
                  <h3 className="text-xl font-bold text-white">Additional Target Banking Partners</h3>
                  <span className="bg-yellow-900 text-yellow-300 text-xs font-bold px-3 py-1 rounded-full border border-yellow-600">Partnerships Planned</span>
                </div>
                <div className="space-y-4">
                  {[
                    { name: 'Metropolitan Commercial Bank (FDIC)', role: 'Payment processing and business banking services', reason: 'Proven fintech banking infrastructure with strong compliance posture' },
                    { name: 'Evolve Bank & Trust (FDIC)', role: 'Card issuing and program management', reason: 'Leading BaaS provider with established card program management capabilities' },
                    { name: 'Cross River Bank (FDIC)', role: 'API banking and compliance infrastructure', reason: 'Technology-first bank with purpose-built fintech APIs and regulatory expertise' },
                  ].map(({ name, role, reason }) => (
                    <div key={name} className="border-l-2 border-blue-500 pl-4">
                      <p className="font-bold text-white">{name}</p>
                      <p className="text-sm text-purple-200">{role}</p>
                      <p className="text-xs text-purple-400 mt-1">Selection rationale: {reason}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-purple-900 bg-opacity-60 p-6 rounded-lg">
                <div className="flex items-center flex-wrap gap-2 mb-4">
                  <h3 className="text-xl font-bold text-white">Insurance Coverage Roadmap</h3>
                  <span className="bg-yellow-900 text-yellow-300 text-xs font-bold px-3 py-1 rounded-full border border-yellow-600">Planned Post-Funding</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {[
                    { type: 'Cyber Insurance', target: '$50M+ coverage', provider: 'Target: Lloyd\'s of London syndicate', why: 'Protection against data breaches, cyber attacks and digital asset theft' },
                    { type: 'Crime & Fidelity Insurance', target: '$100M+ coverage', provider: 'Target: AIG or equivalent A-rated insurer', why: 'Coverage against internal fraud, employee dishonesty and theft of funds' },
                    { type: 'Professional Liability', target: '$25M+ coverage', provider: 'Target: Chubb or equivalent', why: 'Errors & omissions coverage for financial services and technology operations' },
                    { type: 'Crypto Custody Insurance', target: '$200M+ coverage', provider: 'Target: BitGo Trust or equivalent', why: 'Cold and hot wallet coverage for all digital asset reserves held on behalf of customers' },
                  ].map(({ type, target, provider, why }) => (
                    <div key={type} className="bg-purple-800 bg-opacity-60 p-4 rounded-lg border border-purple-600">
                      <p className="font-bold text-white mb-1">{type}</p>
                      <p className="text-green-400 font-semibold text-xs mb-1">{target}</p>
                      <p className="text-purple-300 text-xs mb-2">{provider}</p>
                      <p className="text-purple-400 text-xs">{why}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 - Professional Memberships */}
          <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Users className="mr-3 text-purple-400" size={32} />
              6. Professional Memberships & Associations
            </h2>

            <div className="bg-purple-900 bg-opacity-40 border border-purple-500 rounded-lg p-4 mb-6">
              <p className="text-sm text-purple-200">
                <strong>🌐 Industry Engagement Strategy:</strong> Active membership in key industry associations is central to our regulatory strategy and network development. The organizations below represent our planned memberships upon launch — selected for their influence on fintech policy, compliance best practices, and peer networks.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-purple-200">
              {[
                { name: 'Money Services Round Table (MSRT)', value: 'Direct access to MSB regulatory dialogue and advocacy with US state regulators and FinCEN.' },
                { name: 'Electronic Transactions Association (ETA)', value: 'Leading payments industry association with resources on compliance, fraud prevention and emerging payments standards.' },
                { name: 'Blockchain Association', value: 'Premier US crypto policy advocacy group working directly with Congress and federal regulators on digital asset frameworks.' },
                { name: 'Global Digital Finance (GDF)', value: 'International standards body for crypto and digital assets — alignment signals institutional credibility to global partners.' },
                { name: 'American Bankers Association (ABA)', value: 'Builds our banking relationship credibility and provides access to regulatory developments in traditional finance.' },
                { name: 'Financial Technology Association (FTA)', value: 'Washington DC-based fintech policy group with direct influence on consumer financial protection regulation.' },
              ].map(({ name, value }) => (
                <div key={name} className="bg-purple-900 bg-opacity-60 p-6 rounded-lg border border-purple-700 hover:border-purple-500 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0"></span>
                    <h4 className="font-bold text-white">{name}</h4>
                  </div>
                  <p className="text-sm text-purple-300 leading-relaxed">{value}</p>
                  <p className="text-xs text-yellow-400 font-semibold mt-3">⏱ Membership planned post-incorporation</p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 7 - Audits & Reports */}
          <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <FileText className="mr-3 text-orange-400" size={32} />
              7. Audit & Examination Roadmap
            </h2>

            <div className="bg-orange-900 bg-opacity-40 border border-orange-600 rounded-lg p-4 mb-6">
              <p className="text-sm text-orange-200">
                <strong>📊 Transparency Commitment:</strong> ZANORS is committed to independent verification and full regulatory transparency from the outset. Our audit roadmap is structured to meet the expectations of institutional partners, regulators, and investors from day one of operations.
              </p>
            </div>
            
            <div className="space-y-6 text-purple-200">
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Independent Audit Plan</h3>
                <div className="space-y-4">
                  {[
                    {
                      title: 'Financial Statement Audit',
                      firm: 'Target: Big 4 or top-tier CPA firm (Ernst & Young, KPMG, or equivalent)',
                      frequency: 'Annual — mandatory from first full operating year',
                      scope: 'Full financial statement audit providing unqualified opinion for regulatory reporting and investor transparency.',
                      timeline: 'First audit: End of Year 1 operations',
                    },
                    {
                      title: 'AML/BSA Compliance Audit',
                      firm: 'Target: Specialist AML compliance firm',
                      frequency: 'Annual — required by FinCEN MSB registration',
                      scope: 'Independent review of all anti-money laundering and Bank Secrecy Act controls, policies, and transaction monitoring systems.',
                      timeline: 'First audit: Within 12 months of FinCEN registration',
                    },
                    {
                      title: 'Cryptocurrency Reserve Attestation',
                      firm: 'Target: Deloitte, Armanino, or crypto-specialist auditor',
                      frequency: 'Quarterly — demonstrates 1:1 reserve backing to customers',
                      scope: 'Third-party on-chain and off-chain verification of all digital assets held in custody, confirming 100% reserve backing of customer balances.',
                      timeline: 'First attestation: Within 90 days of launch',
                    },
                  ].map(({ title, firm, frequency, scope, timeline }) => (
                    <div key={title} className="bg-purple-900 bg-opacity-60 p-5 rounded-lg border border-purple-700">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="text-yellow-400 flex-shrink-0" size={18} />
                        <p className="font-bold text-white">{title}</p>
                        <span className="bg-orange-900 text-orange-300 text-xs font-bold px-2 py-0.5 rounded-full border border-orange-700 ml-auto">Planned</span>
                      </div>
                      <p className="text-sm text-purple-300 mb-1"><strong className="text-purple-100">Firm:</strong> {firm}</p>
                      <p className="text-sm text-purple-300 mb-1"><strong className="text-purple-100">Frequency:</strong> {frequency}</p>
                      <p className="text-sm text-purple-400 mb-2">{scope}</p>
                      <p className="text-xs text-yellow-400 font-semibold">⏱ {timeline}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3">Regulatory Examination Readiness</h3>
                <div className="space-y-3">
                  {[
                    {
                      title: 'State Regulatory Examinations',
                      desc: 'Upon obtaining state money transmitter licenses, ZANORS will be subject to periodic examinations by state banking departments. Our compliance infrastructure is being designed to ensure examination-ready operations from day one.',
                    },
                    {
                      title: 'FinCEN Compliance Reviews',
                      desc: 'As a registered MSB, ZANORS will maintain comprehensive BSA/AML records and reporting to support FinCEN compliance reviews. Our transaction monitoring and SAR filing systems are being built to exceed minimum requirements.',
                    },
                  ].map(({ title, desc }) => (
                    <div key={title} className="bg-purple-900 bg-opacity-60 p-4 rounded-lg border border-purple-700">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="text-cyan-400 flex-shrink-0" size={18} />
                        <p className="font-bold text-white">{title}</p>
                      </div>
                      <p className="text-sm text-purple-300">{desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section 8 - Document Verification */}
          <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <BadgeCheck className="mr-3 text-green-400" size={32} />
              8. License Verification
            </h2>
            
            <div className="space-y-6 text-purple-200">
              <p>
                All ZANORS licenses and certifications can be independently verified through the respective regulatory authorities:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-purple-900 bg-opacity-60 p-6 rounded-lg">
                  <h4 className="font-bold text-white mb-3">FinCEN</h4>
                  <p className="text-sm mb-2">Verify MSB Registration</p>
                  <a href="https://www.fincen.gov/msb-registrant-search" className="text-blue-400 hover:text-blue-300 text-sm">
                    fincen.gov/msb-registrant-search
                  </a>
                </div>

                <div className="bg-purple-900 bg-opacity-60 p-6 rounded-lg">
                  <h4 className="font-bold text-white mb-3">NMLS</h4>
                  <p className="text-sm mb-2">Consumer Access Database</p>
                  <a href="https://www.nmlsconsumeraccess.org" className="text-blue-400 hover:text-blue-300 text-sm">
                    nmlsconsumeraccess.org
                  </a>
                </div>

                <div className="bg-purple-900 bg-opacity-60 p-6 rounded-lg">
                  <h4 className="font-bold text-white mb-3">NYDFS</h4>
                  <p className="text-sm mb-2">BitLicense Registry</p>
                  <a href="https://www.dfs.ny.gov" className="text-blue-400 hover:text-blue-300 text-sm">
                    dfs.ny.gov
                  </a>
                </div>

                <div className="bg-purple-900 bg-opacity-60 p-6 rounded-lg">
                  <h4 className="font-bold text-white mb-3">FDIC</h4>
                  <p className="text-sm mb-2">BankFind Suite</p>
                  <a href="https://banks.data.fdic.gov/bankfind-suite" className="text-blue-400 hover:text-blue-300 text-sm">
                    banks.data.fdic.gov/bankfind-suite
                  </a>
                </div>
              </div>

              <div className="bg-blue-900 bg-opacity-40 border border-blue-600 rounded-lg p-6 mt-6">
                <p className="text-sm">
                  <strong>Note:</strong> For license verification assistance or to request official documentation, please contact our compliance team at compliance@zanors.com
                </p>
              </div>
            </div>
          </div>

          {/* Section 9 - Contact */}
          <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Mail className="mr-3 text-cyan-400" size={32} />
              9. License Inquiries
            </h2>
            
            <div className="space-y-6 text-purple-200">
              <p>
                For questions about our licenses, certifications, or to request official documentation:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-purple-900 bg-opacity-60 p-6 rounded-lg">
                  <h4 className="font-bold text-white mb-4">Licensing Department</h4>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center">
                      <Mail className="mr-2" size={16} />
                      licensing@zanors.com
                    </p>
                    <p className="flex items-center">
                      <PhoneCall className="mr-2" size={16} />
                      +1-800-555-LICENSE
                    </p>
                  </div>
                </div>

                <div className="bg-purple-900 bg-opacity-60 p-6 rounded-lg">
                  <h4 className="font-bold text-white mb-4">Compliance Officer</h4>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center">
                      <Mail className="mr-2" size={16} />
                      compliance@zanors.com
                    </p>
                    <p className="text-purple-300">For regulatory compliance questions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Statement */}
          <div className="bg-gradient-to-r from-yellow-900 to-green-900 rounded-lg p-8 border-2 border-yellow-500">
            <div className="flex items-start">
              <Award className="mr-4 flex-shrink-0 text-yellow-400" size={32} />
              <div>
                <h3 className="text-2xl font-bold mb-3">Built on a Foundation of Compliance</h3>
                <p className="text-purple-200 mb-4">
                  ZANORS is a pre-launch fintech startup building its regulatory framework before going live — not after. Every system, every process, and every partnership is being designed with the compliance standard of a fully licensed MSB in mind. We believe the right way to build a financial platform is to do the hard work of regulation first.
                </p>
                <p className="text-purple-300 text-sm">
                  This page reflects our compliance roadmap and planned licensing strategy. We are committed to full transparency with our users, investors, and regulators — and will update this page as each milestone is achieved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black bg-opacity-50 mt-16 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-lg mb-4">Services</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Services.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Company.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Support</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Support.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Legal</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Legal.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-purple-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <div className="w-14 h-14 rounded-full flex items-center justify-center p-2 border border-purple-600 shadow-lg">
                  <img src="/zanors-icon.png" alt="Zanors Icon" className="w-full h-full object-contain" />
                </div>
                <div>
                  <span className="text-white font-bold">Zanors</span>
                  <p className="text-xs text-purple-400">ZANORS is a financial technology platform. ZANORS is not an FDIC-insured Bank or depositary</p>
                  <p className="text-xs text-purple-400">taking institution. In the USA, banking services for the placement of deposits where the customers'</p>
                  <p className="text-xs text-purple-400">funds may be placed are provided by BankProv, Member FDIC. Deposit insurance coverage only</p>
                  <p className="text-xs text-purple-400">protects against the failure of an FDIC insured depository institution. Crypto assets and</p>
                  <p className="text-xs text-purple-400">non-bank digital wallet are not insured by the FDIC, are not deposits, and may lose value.</p>
                </div>
              </div>
              
              <div className="text-center md:text-right">
                <p className="text-purple-300 text-sm">
                  © 2026 Zanors Inc. All rights reserved.
                </p>
                <p className="text-purple-400 text-xs mt-1">
                  Licensed by FinCEN • Regulated by NMLS • Member FDIC
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
       <SignupModal 
        showSignupModal={showSignupModal}
        setShowSignupModal={setShowSignupModal}
        signupStep={signupStep}
        setSignupStep={setSignupStep}
        signupData={signupData}
        setSignupData={setSignupData}
        countries={countries}
        generateAuthCode={generateAuthCode}
        setShowLandingPage={setShowLandingPage}
      />
      <LoginModal 
        showLoginModal={showLoginModal}
        setShowLoginModal={setShowLoginModal}
        loginStep={loginStep}
        setLoginStep={setLoginStep}
        loginData={loginData}
        setLoginData={setLoginData}
        generateAuthCode={generateAuthCode}
        setShowLandingPage={setShowLandingPage}
      />
      <ActivateCardModal 
        showActivateCardModal={showActivateCardModal}
        setShowActivateCardModal={setShowActivateCardModal}
        activateData={activateData}
        setActivateData={setActivateData}
        generateAuthCode={generateAuthCode}
        user={null}
        signupData={signupData}
        loginData={loginData}
        setShowLandingPage={setShowLandingPage}
      />
    </div>
  );
};

export default LicensesPage;